import { DatePicker } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

type Props = {
	name: string;
	label?: string;
	value: string;
	required: boolean;
	isError: boolean;
	onChange: (value: string) => void;
	placeholder?: string;
	helperText?: string;
	disabled?: boolean;
	picker?: 'date' | 'month' | 'year';
	disabledBeforeDates?: any;
	disabledAfterDates?: any;
	isViewOnly?: boolean;
	disableSunday?: boolean;
	style?: any;
	allowClear?: boolean;
	// New props for navigation arrows
	showNavigationArrows?: boolean;
	onNavigateBack?: () => void;
	onNavigateForward?: () => void;
	navigationStep?: 'day' | 'month' | 'year';
	disableBackNavigation?: boolean;
	disableForwardNavigation?: boolean;
};

const DatePickerField = (props: Props) => {
	const {
		name,
		label,
		value,
		placeholder,
		isError = false,
		required = false,
		onChange,
		disabled = false,
		helperText = 'Date is required',
		picker = 'date',
		disabledBeforeDates,
		disabledAfterDates,
		isViewOnly = false,
		disableSunday = false,
		style,
		allowClear = false,
		// Navigation props
		showNavigationArrows = false,
		onNavigateBack,
		onNavigateForward,
		navigationStep = 'day',
		disableBackNavigation = false,
		disableForwardNavigation = false,
	} = props;

	const handleChange = (value: Dayjs | null) => {
		onChange(value ? value.toISOString() : '');
	};

	const handleNavigateBack = () => {
		if (onNavigateBack) {
			onNavigateBack();
		} else if (value) {
			// Default navigation behavior
			const currentDate = dayjs(value);
			let newDate;

			switch (navigationStep) {
				case 'day':
					newDate = currentDate.subtract(1, 'day');
					break;
				case 'month':
					newDate = currentDate.subtract(1, 'month');
					break;
				case 'year':
					newDate = currentDate.subtract(1, 'year');
					break;
				default:
					newDate = currentDate.subtract(1, 'day');
			}

			onChange(newDate.toISOString());
		}
	};

	const handleNavigateForward = () => {
		if (onNavigateForward) {
			onNavigateForward();
		} else if (value) {
			// Default navigation behavior
			const currentDate = dayjs(value);
			let newDate;

			switch (navigationStep) {
				case 'day':
					newDate = currentDate.add(1, 'day');
					break;
				case 'month':
					newDate = currentDate.add(1, 'month');
					break;
				case 'year':
					newDate = currentDate.add(1, 'year');
					break;
				default:
					newDate = currentDate.add(1, 'day');
			}

			onChange(newDate.toISOString());
		}
	};

	// Check if navigation should be disabled based on date constraints
	const isBackDisabled = () => {
		if (disableBackNavigation || disabled) return true;
		if (disabledBeforeDates && value) {
			const currentDate = dayjs(value);
			const minDate = dayjs(disabledBeforeDates);
			let previousDate;

			switch (navigationStep) {
				case 'day':
					previousDate = currentDate.subtract(1, 'day');
					break;
				case 'month':
					previousDate = currentDate.subtract(1, 'month');
					break;
				case 'year':
					previousDate = currentDate.subtract(1, 'year');
					break;
				default:
					previousDate = currentDate.subtract(1, 'day');
			}

			return previousDate.isBefore(minDate);
		}
		return false;
	};

	const isForwardDisabled = () => {
		if (disableForwardNavigation || disabled) return true;
		if (disabledAfterDates && value) {
			const currentDate = dayjs(value);
			const maxDate = dayjs(disabledAfterDates);
			let nextDate;

			switch (navigationStep) {
				case 'day':
					nextDate = currentDate.add(1, 'day');
					break;
				case 'month':
					nextDate = currentDate.add(1, 'month');
					break;
				case 'year':
					nextDate = currentDate.add(1, 'year');
					break;
				default:
					nextDate = currentDate.add(1, 'day');
			}

			return nextDate.isAfter(maxDate);
		}
		return false;
	};

	return (
		<div className="input-field" style={{ ...style }}>
			{label && (
				<p
					className="label"
					style={{ marginBottom: `${isViewOnly ? '5px' : ''}` }}
				>
					{label}{' '}
					{required && !isViewOnly && <span className="red">*</span>}
				</p>
			)}
			{isViewOnly ? (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
					}}
				>
					{showNavigationArrows && (
						<button
							type="button"
							onClick={handleNavigateBack}
							disabled={isBackDisabled()}
							style={{
								border: 'none',
								background: 'transparent',
								cursor: isBackDisabled()
									? 'not-allowed'
									: 'pointer',
								padding: '4px',
								opacity: isBackDisabled() ? 0.5 : 1,
							}}
						>
							<LeftOutlined />
						</button>
					)}
					<p style={{ margin: 0, minWidth: 'fit-content' }}>
						{dayjs(value).format(
							picker === 'year'
								? 'YYYY'
								: picker === 'month'
								? 'MMM, YYYY'
								: 'DD/MM/YYYY'
						)}
					</p>
					{showNavigationArrows && (
						<button
							type="button"
							onClick={handleNavigateForward}
							disabled={isForwardDisabled()}
							style={{
								border: 'none',
								background: 'transparent',
								cursor: isForwardDisabled()
									? 'not-allowed'
									: 'pointer',
								padding: '4px',
								opacity: isForwardDisabled() ? 0.5 : 1,
							}}
						>
							<RightOutlined />
						</button>
					)}
				</div>
			) : (
				<div>
					{showNavigationArrows ? (
						<div style={{ position: 'relative', width: '100%' }}>
							<DatePicker
								name={name}
								value={value ? dayjs(value) : null}
								size="large"
								onChange={handleChange}
								format={
									picker === 'year'
										? 'YYYY'
										: picker === 'month'
										? 'MMM, YYYY'
										: 'DD/MM/YYYY'
								}
								disabled={disabled}
								placeholder={placeholder}
								style={{
									width: '100%',
									paddingLeft: '40px',
									paddingRight: '40px',
								}}
								status={isError ? 'error' : ''}
								picker={picker}
								disabledDate={(current: Dayjs) => {
									return (
										(disabledBeforeDates &&
											current <
												dayjs(disabledBeforeDates)) ||
										(disabledAfterDates &&
											current >
												dayjs(disabledAfterDates)) ||
										(disableSunday && current.day() === 0)
									);
								}}
								allowClear={allowClear}
								inputReadOnly={true}
							/>
							{/* Left Arrow */}
							<button
								type="button"
								onClick={handleNavigateBack}
								disabled={isBackDisabled()}
								style={{
									position: 'absolute',
									left: '8px',
									top: '50%',
									transform: 'translateY(-50%)',
									border: 'none',
									background: 'transparent',
									cursor: isBackDisabled()
										? 'not-allowed'
										: 'pointer',
									padding: '4px',
									opacity: isBackDisabled() ? 0.3 : 0.6,
									zIndex: 2,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '14px',
									color: '#666',
									transition: 'opacity 0.2s',
								}}
								onMouseEnter={(e) => {
									if (!isBackDisabled()) {
										e.currentTarget.style.opacity = '1';
									}
								}}
								onMouseLeave={(e) => {
									if (!isBackDisabled()) {
										e.currentTarget.style.opacity = '0.6';
									}
								}}
							>
								<LeftOutlined />
							</button>
							{/* Right Arrow */}
							<button
								type="button"
								onClick={handleNavigateForward}
								disabled={isForwardDisabled()}
								style={{
									position: 'absolute',
									right: allowClear ? '32px' : '8px', // Account for clear button if present
									top: '50%',
									transform: 'translateY(-50%)',
									border: 'none',
									background: 'transparent',
									cursor: isForwardDisabled()
										? 'not-allowed'
										: 'pointer',
									padding: '4px',
									opacity: isForwardDisabled() ? 0.3 : 0.6,
									zIndex: 2,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '14px',
									color: '#666',
									transition: 'opacity 0.2s',
								}}
								onMouseEnter={(e) => {
									if (!isForwardDisabled()) {
										e.currentTarget.style.opacity = '1';
									}
								}}
								onMouseLeave={(e) => {
									if (!isForwardDisabled()) {
										e.currentTarget.style.opacity = '0.6';
									}
								}}
							>
								<RightOutlined />
							</button>
						</div>
					) : (
						<DatePicker
							name={name}
							value={value ? dayjs(value) : null}
							size="large"
							onChange={handleChange}
							format={
								picker === 'year'
									? 'YYYY'
									: picker === 'month'
									? 'MMM, YYYY'
									: 'DD/MM/YYYY'
							}
							disabled={disabled}
							placeholder={placeholder}
							style={{ width: '100%' }}
							status={isError ? 'error' : ''}
							picker={picker}
							disabledDate={(current: Dayjs) => {
								return (
									(disabledBeforeDates &&
										current < dayjs(disabledBeforeDates)) ||
									(disabledAfterDates &&
										current > dayjs(disabledAfterDates)) ||
									(disableSunday && current.day() === 0)
								);
							}}
							allowClear={allowClear}
						/>
					)}
					{isError && (
						<p
							className="red"
							style={{
								fontSize: '12px',
								marginLeft: '2px',
							}}
						>
							{helperText}
						</p>
					)}
				</div>
			)}
		</div>
	);
};

export default DatePickerField;
